import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Settings as SettingsIcon, Database, Mail, UserPlus, UserCheck, Tags, SpellCheck, Workflow, SlidersHorizontal, FolderKanban, Heart, ListChecks } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import InfoTip from '../components/InfoTip';
import { GlossaryId } from '../content/glossary';
import { navLabel } from '../utils/navigation';
import { useTabParam } from '../hooks/useTabParam';
import { useAuth } from '../contexts/AuthContext';
import UpdateDataTab from '../components/settings/UpdateDataTab';
import JiraConfigTab from '../components/settings/JiraConfigTab';
import SmtpConfigTab from '../components/settings/SmtpConfigTab';
import CreateUsersTab from '../components/settings/CreateUsersTab';
import ManageTeamsTab from '../components/settings/ManageTeamsTab';
import ProjectsTab from '../components/settings/ProjectsTab';
import KudosTab from '../components/settings/KudosTab';
import JiraFieldMappingTab from '../components/settings/JiraFieldMappingTab';
import NamingRulesTab from '../components/settings/NamingRulesTab';
import FlowSettingsTab from '../components/settings/FlowSettingsTab';
import StandardsTab from '../components/settings/StandardsTab';
import PlanImportTab from '../components/settings/PlanImportTab';

type TabId = 'update-data' | 'jira-config' | 'field-mapping' | 'naming-rules' | 'flow' | 'standards' | 'smtp-config' | 'create-users' | 'manage-teams' | 'projects' | 'kudos' | 'plan-import';

type TabGroup = 'Jira connection' | 'Rules and limits' | 'People';

// Grouped by the job they do. "Rules and limits" is not called Standards: one of its tabs already is.
const TABS: { id: TabId; label: string; group: TabGroup; description: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: 'update-data', label: 'Update data', group: 'Jira connection', description: 'Sync data from Jira', icon: Database },
  { id: 'jira-config', label: 'Jira configuration', group: 'Jira connection', description: 'Configure Jira credentials', icon: SettingsIcon },
  { id: 'field-mapping', label: 'Field mapping', group: 'Jira connection', description: 'Map logical planning fields to Jira custom fields', icon: Tags },
  { id: 'smtp-config', label: 'SMTP / email', group: 'Jira connection', description: 'Configure email server', icon: Mail },
  { id: 'naming-rules', label: 'Naming rules', group: 'Rules and limits', description: 'Checks and wording for ticket titles', icon: SpellCheck },
  { id: 'flow', label: 'Flow', group: 'Rules and limits', description: 'Roles and stage owners', icon: Workflow },
  { id: 'standards', label: 'Standards', group: 'Rules and limits', description: 'Limits and worklog tags', icon: SlidersHorizontal },
  { id: 'plan-import', label: 'Plan import', group: 'Rules and limits', description: "Bring the planning sheet's rows into the Plan", icon: ListChecks },
  { id: 'create-users', label: 'Create users', group: 'People', description: 'Manage user accounts', icon: UserPlus },
  { id: 'manage-teams', label: 'Manage teams', group: 'People', description: 'Create and manage teams', icon: UserCheck },
  { id: 'projects', label: 'Projects', group: 'People', description: 'Who owns each project and who is involved', icon: FolderKanban },
  { id: 'kudos', label: 'Kudos', group: 'People', description: 'Find a kudos and take it back', icon: Heart },
];

const TAB_GROUPS: TabGroup[] = ['Jira connection', 'Rules and limits', 'People'];

const TAB_TIPS: Record<TabId, GlossaryId> = {
  'update-data': 'settings.update-data',
  'jira-config': 'settings.jira-config',
  'field-mapping': 'settings.field-mapping',
  'smtp-config': 'settings.smtp-config',
  'naming-rules': 'settings.naming-rules',
  flow: 'settings.flow',
  standards: 'settings.standards',
  'create-users': 'settings.create-users',
  'manage-teams': 'settings.manage-teams',
  projects: 'settings.projects',
  kudos: 'settings.kudos',
  'plan-import': 'settings.plan-import',
};

export default function SettingsPage() {
  const { user } = useAuth();
  // The open tab is in the address (/settings?tab=flow), so a link, a reload and Back all keep it.
  const [activeTab, setActiveTab] = useTabParam<TabId>(TABS.map((t) => t.id), 'update-data');

  // Sliding underline indicator that tracks the active tab button.
  const tabRefs = useRef<Partial<Record<TabId, HTMLButtonElement | null>>>({});
  const [indicator, setIndicator] = useState<{ left: number; width: number }>({ left: 0, width: 0 });

  const syncIndicator = React.useCallback(() => {
    const el = tabRefs.current[activeTab];
    if (el) setIndicator({ left: el.offsetLeft, width: el.offsetWidth });
  }, [activeTab]);

  useLayoutEffect(syncIndicator, [syncIndicator]);

  useEffect(() => {
    window.addEventListener('resize', syncIndicator);
    return () => window.removeEventListener('resize', syncIndicator);
  }, [syncIndicator]);

  if (user?.role !== 'admin') return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader title={navLabel('/settings')} subtitle="Manage your Jira worklog system" icon={SettingsIcon} />
      <div className="p-4 sm:p-6">
        <div className="max-w-7xl mx-auto">
        {/* Top tab bar */}
        <div className="mb-6 flex items-end border-b border-gray-200">
          <div className="relative flex min-w-0 flex-1 gap-6 overflow-x-auto">
            {TAB_GROUPS.map(group => (
              <div key={group} role="group" aria-label={group} className="flex flex-col">
                <p className="px-4 pt-1 text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-500 whitespace-nowrap">{group}</p>
                <div className="flex gap-1">
                  {TABS.filter(tab => tab.group === group).map(tab => {
                    const Icon = tab.icon;
                    const active = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        ref={el => { tabRefs.current[tab.id] = el; }}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors ${active ? 'text-blue-700' : 'text-gray-600 hover:text-gray-900'}`}
                      >
                        <Icon className="w-4 h-4" />
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            {/* Sliding active-tab underline */}
            <span
              className="pointer-events-none absolute bottom-0 h-0.5 rounded-full bg-blue-600 transition-all duration-300 ease-out"
              style={{ left: indicator.left, width: indicator.width }}
            />
          </div>
          <span className="shrink-0 self-center pl-2"><InfoTip id={TAB_TIPS[activeTab]} className="" /></span>
        </div>

        {/* Active tab content */}
        <div>
          {activeTab === 'update-data' && <UpdateDataTab />}
          {activeTab === 'jira-config' && <JiraConfigTab />}
          {activeTab === 'field-mapping' && <JiraFieldMappingTab />}
          {activeTab === 'naming-rules' && <NamingRulesTab />}
          {activeTab === 'flow' && <FlowSettingsTab />}
          {activeTab === 'standards' && <StandardsTab />}
          {activeTab === 'smtp-config' && <SmtpConfigTab />}
          {activeTab === 'create-users' && <CreateUsersTab />}
          {activeTab === 'manage-teams' && <ManageTeamsTab />}
          {activeTab === 'projects' && <ProjectsTab />}
          {activeTab === 'kudos' && <KudosTab />}
          {activeTab === 'plan-import' && <PlanImportTab />}
        </div>
        </div>
      </div>
    </div>
  );
}
